// In-document edits: user changes to the curated report content, keyed per
// item. "" means the item was removed; a missing key means untouched. Every
// edit is captured (rams_edit events + the rams_download snapshot) — the
// labelled diff between curated content and the user's real job is the core
// alignment signal for improving the reports.
import { splitEmergency, type RamsReport } from "./reports";

export type DocEdits = Record<string, string>;

export const SCOPE_KEY = "scope";
export const TITLE_KEY = "title";
export const ctlKey = (hazardId: string, index: number) => `ctl:${hazardId}:${index}`;
export const seqKey = (order: number) => `seq:${order}`;
export const whoKey = (hazardId: string) => `who:${hazardId}`;
export const ppeKey = (index: number) => `ppe:${index}`;
export const subKey = (index: number) => `sub:${index}`;
export const compKey = (index: number) => `comp:${index}`;
export const emgKey = (index: number) => `emg:${index}`;

/** Keys whose item is not removable — emptying them restores the original
 *  instead (a document without a title/scope/who-at-risk is invalid). */
export const isNonRemovable = (key: string) =>
  key === SCOPE_KEY || key === TITLE_KEY || key.startsWith("who:");

/** Original curated text for an edit key (for diffs and restore). */
export function originalText(report: RamsReport, key: string): string {
  if (key === SCOPE_KEY) return report.scopeOfWorks;
  if (key === TITLE_KEY) return report.title;
  const ctl = key.match(/^ctl:(.+):(\d+)$/);
  if (ctl) return report.hazards.find((h) => h.hazardId === ctl[1])?.controls[Number(ctl[2])] ?? "";
  const seq = key.match(/^seq:(\d+)$/);
  if (seq) return report.sequenceOfWorks.find((s) => s.order === Number(seq[1]))?.description ?? "";
  const who = key.match(/^who:(.+)$/);
  if (who) return report.hazards.find((h) => h.hazardId === who[1])?.whoIsAtRisk.join(", ") ?? "";
  const ppe = key.match(/^ppe:(\d+)$/);
  if (ppe) return report.ppe[Number(ppe[1])] ?? "";
  const sub = key.match(/^sub:(\d+)$/);
  if (sub) return report.substances[Number(sub[1])] ?? "";
  const comp = key.match(/^comp:(\d+)$/);
  if (comp) return report.competence[Number(comp[1])]?.requirement ?? "";
  const emg = key.match(/^emg:(\d+)$/);
  if (emg) return splitEmergency(report.emergencyRescue)[Number(emg[1])]?.body ?? "";
  return "";
}

/** Report with the user's edits applied — removed items filtered, sequence
 *  renumbered. This is what the PDF renders. */
export function applyEdits(report: RamsReport, edits: DocEdits): RamsReport {
  if (!Object.keys(edits).length) return report;
  const scope = (edits[SCOPE_KEY] ?? "").trim();
  const title = (edits[TITLE_KEY] ?? "").trim();
  const emergencyRescue = (() => {
    const sections = splitEmergency(report.emergencyRescue);
    if (!sections.some((_, i) => edits[emgKey(i)] !== undefined)) return report.emergencyRescue;
    return sections
      .map((s, i) => ({ ...s, body: (edits[emgKey(i)] ?? s.body).trim() }))
      .filter((s) => s.body !== "")
      .map((s) => (s.heading ? `${s.heading}: ${s.body}` : s.body))
      .join(" ");
  })();
  return {
    ...report,
    title: edits[TITLE_KEY] !== undefined && title ? title : report.title,
    scopeOfWorks: edits[SCOPE_KEY] !== undefined && scope ? scope : report.scopeOfWorks,
    hazards: report.hazards.map((h) => {
      const whoEdit = edits[whoKey(h.hazardId)];
      const who = whoEdit !== undefined ? whoEdit.split(",").map((w) => w.trim()).filter(Boolean) : h.whoIsAtRisk;
      return {
        ...h,
        whoIsAtRisk: who.length ? who : h.whoIsAtRisk, // never strip entirely
        controls: h.controls
          .map((c, i) => edits[ctlKey(h.hazardId, i)] ?? c)
          .filter((c) => c.trim() !== ""),
      };
    }),
    sequenceOfWorks: report.sequenceOfWorks
      .map((s) => ({ ...s, description: edits[seqKey(s.order)] ?? s.description }))
      .filter((s) => s.description.trim() !== "")
      .map((s, i) => ({ ...s, order: i + 1 })),
    ppe: report.ppe.map((p, i) => edits[ppeKey(i)] ?? p).filter((p) => p.trim() !== ""),
    substances: report.substances.map((s, i) => edits[subKey(i)] ?? s).filter((s) => s.trim() !== ""),
    competence: report.competence
      .map((c, i) => ({ ...c, requirement: edits[compKey(i)] ?? c.requirement }))
      .filter((c) => c.requirement.trim() !== ""),
    emergencyRescue,
  };
}

/** Compact diff list for the download snapshot (kept under the event-data cap). */
export function editsSummary(report: RamsReport, edits: DocEdits): { key: string; from: string; to: string }[] {
  return Object.entries(edits)
    .slice(0, 12)
    .map(([key, to]) => ({
      key,
      from: originalText(report, key).substring(0, 120),
      to: to.substring(0, 120),
    }));
}
